import React, { useMemo, useState } from 'react'
import { Box, Chip, Tab, Tabs, Typography } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'

import { InventoryIssueCard } from '../MoveOut/InventoryIssueCard'
import { InspectionDetail } from '../MoveOut/types'
import {
    countNotableItems,
    isInventoryItemNotable,
    ResultsFilterMode,
} from './inspectionUtils'

interface InspectionAreaResultsProps {
    inspections: InspectionDetail[]
    showVideos?: boolean
    defaultTab?: 'issues' | 'all'
    /** How to determine notable items on the Issues/Flagged tab */
    filterMode?: ResultsFilterMode
    /** When true, only show the full inventory list (no Issues/Flagged tab). */
    hideNotableTab?: boolean
}

const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const AreaVideoSection: React.FC<{
    inspection: InspectionDetail
}> = ({ inspection }) => {
    if ((inspection.videos ?? []).length === 0) {
        return null
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {(inspection.videos ?? []).map((video) => (
                <Box
                    key={video.id}
                    sx={{
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#fff' }}
                        >
                            Inspection Video
                        </Typography>
                    </Box>
                    <Box sx={{ backgroundColor: '#000' }}>
                        <video
                            controls
                            style={{
                                width: '100%',
                                maxHeight: 400,
                                display: 'block',
                            }}
                        >
                            <source src={video.url} />
                        </video>
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

const AreaInventoryList: React.FC<{
    inspection: InspectionDetail
    notableOnly: boolean
    filterMode: ResultsFilterMode
}> = ({ inspection, notableOnly, filterMode }) => {
    const items = inspection.inventory_inspections ?? []
    const filtered = notableOnly
        ? items.filter((inv) => isInventoryItemNotable(inv, filterMode))
        : items

    const emptyNotableMessage =
        filterMode === 'flagged'
            ? 'No flagged items in this area.'
            : 'No issues reported in this area.'

    if (filtered.length === 0) {
        return (
            <Box
                sx={{
                    bgcolor: 'rgba(138, 142, 140, 0.16)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.1)',
                    p: 2,
                }}
            >
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    {notableOnly
                        ? emptyNotableMessage
                        : 'No inventory items recorded.'}
                </Typography>
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filtered.map((inv) => (
                <InventoryIssueCard key={inv.id} inventoryInspection={inv} />
            ))}
        </Box>
    )
}

export const InspectionAreaResults: React.FC<InspectionAreaResultsProps> = ({
    inspections,
    showVideos = true,
    defaultTab = 'issues',
    filterMode = 'tenant_issues',
    hideNotableTab = false,
}) => {
    const [tab, setTab] = useState<'issues' | 'all'>(hideNotableTab ? 'all' : defaultTab)
    const [selectedAreaId, setSelectedAreaId] = useState<number | 'all'>('all')

    const isFlaggedMode = filterMode === 'flagged'
    const notableCount = useMemo(
        () => countNotableItems(inspections, filterMode),
        [inspections, filterMode],
    )

    const filteredInspections = useMemo(() => {
        if (selectedAreaId === 'all') {
            return inspections
        }
        return inspections.filter((i) => i.id === selectedAreaId)
    }, [inspections, selectedAreaId])

    if (inspections.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                No inspection results available.
            </Typography>
        )
    }

    const hasMultipleAreas = inspections.length > 1
    const notableTabLabel = isFlaggedMode ? 'Flagged' : 'Issues'

    return (
        <Box>
            {!hideNotableTab && (
                <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    sx={{
                        mb: 2,
                        minHeight: 36,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            minHeight: 36,
                            color: 'rgba(255,255,255,0.5)',
                            fontWeight: 500,
                        },
                        '& .Mui-selected': { color: '#fff' },
                        '& .MuiTabs-indicator': {
                            backgroundColor: 'primary.main',
                        },
                    }}
                >
                    <Tab
                        value="issues"
                        label={`${notableTabLabel}${notableCount > 0 ? ` (${notableCount})` : ''}`}
                    />
                    <Tab value="all" label="All items" />
                </Tabs>
            )}

            {hideNotableTab && (
                <Typography
                    variant="subtitle2"
                    sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, mb: 2 }}
                >
                    All inspected items
                </Typography>
            )}

            {hasMultipleAreas && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        pb: 1,
                        mb: 2,
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'rgba(255,255,255,0.2)',
                            borderRadius: 2,
                        },
                    }}
                >
                    <Chip
                        label="All areas"
                        size="small"
                        clickable
                        onClick={() => setSelectedAreaId('all')}
                        sx={{
                            flexShrink: 0,
                            fontWeight: 600,
                            bgcolor:
                                selectedAreaId === 'all'
                                    ? 'primary.main'
                                    : 'rgba(255,255,255,0.08)',
                            color: '#fff',
                        }}
                    />
                    {inspections.map((inspection) => (
                        <Chip
                            key={inspection.id}
                            label={inspection.area_name}
                            size="small"
                            clickable
                            onClick={() => setSelectedAreaId(inspection.id)}
                            sx={{
                                flexShrink: 0,
                                fontWeight: 600,
                                bgcolor:
                                    selectedAreaId === inspection.id
                                        ? 'primary.main'
                                        : 'rgba(255,255,255,0.08)',
                                color: '#fff',
                            }}
                        />
                    ))}
                </Box>
            )}

            {!hideNotableTab && tab === 'issues' && notableCount === 0 && (
                <Box
                    sx={{
                        bgcolor: 'rgba(138, 142, 140, 0.16)',
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.1)',
                        p: 3,
                        textAlign: 'center',
                        mb: 2,
                    }}
                >
                    <AssignmentIcon
                        sx={{ fontSize: 32, color: 'success.main', mb: 1 }}
                    />
                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                        {isFlaggedMode ? 'No flagged items' : 'No issues reported'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        All inspected items passed. Switch to &ldquo;All items&rdquo; to
                        see the full inventory.
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {filteredInspections.map((inspection) => (
                    <Box key={inspection.id}>
                        {(selectedAreaId === 'all' || hasMultipleAreas) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, color: '#fff' }}
                                >
                                    {inspection.area_name}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    {inspection.completed_by && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#94a3b8' }}
                                        >
                                            By {inspection.completed_by}
                                        </Typography>
                                    )}
                                    {inspection.completed_date && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#94a3b8' }}
                                        >
                                            Completed{' '}
                                            {formatDate(inspection.completed_date)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {showVideos && <AreaVideoSection inspection={inspection} />}

                        <AreaInventoryList
                            inspection={inspection}
                            notableOnly={!hideNotableTab && tab === 'issues'}
                            filterMode={filterMode}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    )
}
